import { AxiosInstance } from 'axios'
import {
    Attachment,
    BulkDocumentMetadata,
    BulkDocumentResult,
    Document,
    DocumentCreate,
    DocumentSuggestion,
    ListDocumentParams,
    PaginatedDocumentResponse,
} from './document.type'
import { PaginatedUserResponse, ListUsersParams } from '../user/user.type'
import { IDocument } from '../../../../../apps/web/src/shared/api/documents'
import { toast } from '../../../../../packages/ui/src/components/Sonner'
import { FolderTreeResponse } from '../folder'

export class DocumentApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all document with pagination
     */
    async list(
        params?: ListDocumentParams & {
            documentFormat?: string
            documentType?: string
            createdBy?: string
        },
    ): Promise<PaginatedDocumentResponse> {
        const response = await this.client.get<PaginatedDocumentResponse>('/documents', {
            params,
        })
        return response.data
    }

    private toFormData(payload: any): FormData {
        if (payload instanceof FormData) {
            return payload
        }
        const formData = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((val) => {
                        if (val instanceof Blob) {
                            formData.append(key, val)
                        } else if (typeof val === 'object') {
                            formData.append(key, JSON.stringify(val))
                        } else {
                            formData.append(key, String(val))
                        }
                    })
                } else if (value instanceof Blob) {
                    formData.append(key, value)
                } else if (typeof value === 'object') {
                    formData.append(key, JSON.stringify(value))
                } else {
                    formData.append(key, String(value))
                }
            }
        })
        return formData
    }

    /**
     * Create a new project
     */
    async createForm(payload: DocumentCreate | FormData): Promise<Document> {
        const body = this.toFormData(payload)
        const response = await this.client.post<Document>('/documents', body)
        return response.data
    }

    /**
     * Get a single document by ID
     */
    async findOne(id: string): Promise<IDocument> {
        const response = await this.client.get<IDocument>(`/documents/${id}`)
        return response.data
    }

    async create(payload: IDocument): Promise<IDocument> {
        const response = await this.client.post<IDocument>('/documents', payload)
        return response.data
    }

    /**
     * Update a document
     */
    async update(id: string, payload: DocumentCreate | FormData | any): Promise<Document> {
        const body = this.toFormData(payload)
        const response = await this.client.put<Document>(`/documents/${id}`, body)
        return response.data
    }

    /**
     * Delete a project (soft delete)
     * Delete a document
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/documents/${id}`)
    }

    async getDocumentDetail(id: string): Promise<Document> {
        const response = await this.client.get<Document>(`/documents/${id}`)
        return response.data
    }

    async checkDuplicateFiles(payload: { projectId: string; filenames: string[] }): Promise<{ duplicates: string[] }> {
        const response = await this.client.post<{ duplicates: string[] }>('/documents/check-duplicate', payload)
        return response.data
    }

    async getUrlFile(id: string): Promise<string> {
        const response = await this.client.get<{ url: string }>(`/documents/${id}/preview`)
        return response?.data?.url
    }

    async getUrlFilePreview(id: string, fileId: string): Promise<string> {
        try {
            const { data } = await this.client.post<{ ticket: string }>(`/attachments/${fileId}/preview-ticket`)
            const baseUrl = this.client.defaults.baseURL || ''
            return `${baseUrl}/attachments/${fileId}/stream?ticket=${data.ticket}`
        } catch (error) {
            console.error('Failed to get preview ticket:', error)
            return ''
        }
    }

    async getUrlFileDownload(id: string, fileId: string): Promise<string> {
        try {
            const { data } = await this.client.post<{ ticket: string }>(`/attachments/${fileId}/preview-ticket`)
            const baseUrl = this.client.defaults.baseURL || ''
            return `${baseUrl}/attachments/${fileId}/download?ticket=${data.ticket}`
        } catch (error) {
            console.error('Failed to get download ticket:', error)
            return ''
        }
    }

    /**
     * Upload a document with FormData
     */
    async upload(payload: {
        documentCode: string
        title: string
        documentTypeId: string
        description?: string
        files: File[]
    }): Promise<IDocument> {
        const formData = new FormData()

        formData.append('documentCode', payload.documentCode)
        formData.append('title', payload.title)
        formData.append('documentTypeId', payload.documentTypeId)
        if (payload.description) formData.append('description', payload.description)
        payload.files.forEach(file => {
            formData.append('files', file)
        })

        const response = await this.client.post<IDocument>('/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    }

    async download(id: string): Promise<void> {
        try {
            const url = await this.getUrlFile(id)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', '')
            link.setAttribute('rel', 'noopener noreferrer')
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error: any) {
            toast.error({
                title: error?.message || 'Something went wrong.',
            })
        }
    }

    async downloadFile(id: string, fileId: string, filename?: string): Promise<void> {
        try {
            const { data } = await this.client.post<{ ticket: string }>(`/attachments/${fileId}/preview-ticket`)
            const baseUrl = this.client.defaults.baseURL || ''
            const url = `${baseUrl}/attachments/${fileId}/download?ticket=${data.ticket}`

            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename || '')
            link.setAttribute('rel', 'noopener noreferrer')
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error: any) {
            return Promise.reject(error)
        }
    }

    private triggerDownload(blob: Blob, fileName: string) {
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
    }

    private handleDownloadError(error: any) {
        if (error?.response?.status === 403) {
            toast.error({
                title: 'Bạn không có quyền vận hành chức năng này',
            })
            return
        }
        toast.error({
            title: error?.response?.data?.message || error?.message || 'Something went wrong.',
        })
    }

    private getFileNameFromHeader(headers: any) {
        const disposition = headers['content-disposition']
        if (!disposition) return null

        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
        if (utf8Match?.[1]) {
            try {
                return decodeURIComponent(utf8Match[1])
            } catch {
                return utf8Match[1]
            }
        }

        const asciiMatch = disposition.match(/filename="?([^";]+)"?/i)
        if (!asciiMatch?.[1]) return null

        try {
            return decodeURIComponent(asciiMatch[1])
        } catch {
            return asciiMatch[1]
        }
    }

    /**
     * Reorder documents
     */
    async reorder(payload: { items: { id: string; displayOrder: number; folderId: string | null }[] }): Promise<void> {
        await this.client.put('/documents/reorder', payload)
    }

    /**
     * Retry OCR for a failed attachment
     */
    async reload(id: string, attachmentId: string): Promise<any> {
        const response = await this.client.post(`/documents/${id}/reload/${attachmentId}`)
        return response.data
    }

    async getDocumentTree(id: string): Promise<FolderTreeResponse> {
        const response = await this.client.get<FolderTreeResponse>(`/documents/${id}/tree-folders`)
        return response.data
    }

    /**
     * Get users related to a document (scoped)
     */
    async listUsers(documentId: string, params?: ListUsersParams): Promise<PaginatedUserResponse> {
        const response = await this.client.get<PaginatedUserResponse>(`/documents/${documentId}/users`, {
            params,
        })
        return response.data
    }

    async getAttachment(id: string): Promise<Attachment> {
        const response = await this.client.get<Attachment>(`/attachments/${id}`)
        return response.data
    }

    /**
     * Get document version history
     */
    async getVersions(documentId: string, params?: { page?: number; take?: number }): Promise<any> {
        const response = await this.client.get(`/documents/${documentId}/versions`, {
            params,
        })
        return response.data
    }

    /**
     * Get preview URL for a file in a specific version
     */
    async getVersionFilePreview(documentId: string, versionId: string, fileId: string): Promise<string> {
        try {
            const { data } = await this.client.post<{ ticket: string }>(`/attachments/${fileId}/preview-ticket`)
            const baseUrl = this.client.defaults.baseURL || ''
            return `${baseUrl}/attachments/${fileId}/stream?ticket=${data.ticket}`
        } catch (error) {
            console.error('Failed to get version preview ticket:', error)
            return ''
        }
    }

    /**
     * Download a file from a specific version
     */
    async downloadVersionFile(documentId: string, versionId: string, fileId: string, filename?: string): Promise<void> {
        try {
            const url = await this.getUrlFileDownload(documentId, fileId)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename || '')
            link.setAttribute('rel', 'noopener noreferrer')
            link.setAttribute('target', '_blank')
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error: any) {
            this.handleDownloadError(error)
        }
    }

    /**
     * @deprecated Use uploadStream instead. Get pre-signed SAS URL for direct upload to Azure Storage
     * POST /storage/upload-url
     */
    async getUploadUrl(payload: {
        filename: string
        projectId: string
        folderId: string
        documentId: string
        attachmentId: string
    }): Promise<{ sasUrl: string; storagePath: string; blobName: string }> {
        const response = await this.client.post<{ sasUrl: string; storagePath: string; blobName: string }>(
            '/storage/upload-url',
            payload,
        )
        return response.data
    }

    /**
     * Upload stream directly to backend (Zero-SAS)
     * POST /storage/upload-stream
     */
    async uploadStream(
        payload: {
            file: File
            projectId: string
            documentId?: string
            attachmentId?: string
        },
        onUploadProgress?: (progressEvent: any) => void,
    ): Promise<{ storagePath: string; blobName: string }> {
        const formData = new FormData()
        formData.append('projectId', payload.projectId)
        if (payload.documentId) formData.append('documentId', payload.documentId)
        if (payload.attachmentId) formData.append('attachmentId', payload.attachmentId)

        formData.append('file', payload.file)

        const response = await this.client.post<{ storagePath: string; blobName: string }>(
            '/storage/upload-stream',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress,
            },
        )
        return response.data
    }

    /**
     * Get AI-powered metadata suggestions for multiple files (no DB save)
     * POST /documents/suggestions
     */
    async getSuggestions(payload: {
        files?: File[]
        tempIds?: string[]
        providerSuggestion?: 'openai' | 'gemini'
        documents?: {
            attachmentId: string
            storagePath: string
        }[]
    }): Promise<DocumentSuggestion[]> {
        if (!payload.files || payload.files.length === 0) {
            // Send JSON exactly as requested in Step 3 of the guidelines (no tempIds, only documents and providerSuggestion)
            const response = await this.client.post<DocumentSuggestion[]>('/documents/suggestions', {
                documents: payload.documents,
                providerSuggestion: payload.providerSuggestion,
            })
            return response.data
        }

        // Fallback to legacy FormData
        const formData = new FormData()
        payload.files.forEach(file => formData.append('files', file))
        if (payload.tempIds) {
            payload.tempIds.forEach(id => formData.append('tempIds[]', id))
        }
        if (payload.providerSuggestion) {
            formData.append('providerSuggestion', payload.providerSuggestion)
        }
        const response = await this.client.post<DocumentSuggestion[]>('/documents/suggestions', formData)
        return response.data
    }

    /**
     * Bulk create documents with files and metadata
     * POST /documents/bulk
     */
    async bulkCreate(payload: {
        files?: File[]
        tempIds?: string[]
        documents: BulkDocumentMetadata[]
    }): Promise<BulkDocumentResult> {
        // Files are already uploaded directly to Azure via SAS URL,
        // so we send JSON to preserve numeric types (fileSize, etc.)
        // instead of FormData which coerces everything to strings.
        const response = await this.client.post<BulkDocumentResult>('/documents/bulk', {
            documents: payload.documents,
        })
        return response.data
    }
}
